import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Package, ShoppingCart, DollarSign, LayoutDashboard, 
  Plus, Pencil, Trash2, LogOut, Home, Eye, X
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/config';

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productModal, setProductModal] = useState({ open: false, product: null });
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, productsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, getAuthHeaders());
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pockett-purple"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-border p-6 flex flex-col">
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/3d5bnodj_Photoroom_20260131_061208.png"
            alt="Pockett Designs"
            className="h-12"
          />
          <p className="text-sm text-muted-foreground mt-2">Admin Dashboard</p>
        </div>

        <nav className="space-y-2 flex-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'overview' ? 'bg-pockett-purple text-white' : 'hover:bg-muted'
            }`}
            data-testid="nav-overview"
          >
            <LayoutDashboard className="h-5 w-5" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'products' ? 'bg-pockett-purple text-white' : 'hover:bg-muted'
            }`}
            data-testid="nav-products"
          >
            <Package className="h-5 w-5" />
            Products
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'orders' ? 'bg-pockett-purple text-white' : 'hover:bg-muted'
            }`}
            data-testid="nav-orders"
          >
            <ShoppingCart className="h-5 w-5" />
            Orders
          </button>
        </nav>

        <div className="space-y-2">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted transition-colors"
          >
            <Home className="h-5 w-5" />
            View Store
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <h1 className="font-heading font-bold text-3xl text-pockett-gray">Dashboard Overview</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-border/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pockett-purple/10 flex items-center justify-center">
                    <Package className="h-6 w-6 text-pockett-purple" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Total Products</p>
                    <p className="font-heading font-bold text-2xl text-pockett-gray">{stats?.total_products || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-border/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pockett-pink/10 flex items-center justify-center">
                    <ShoppingCart className="h-6 w-6 text-pockett-pink" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Total Orders</p>
                    <p className="font-heading font-bold text-2xl text-pockett-gray">{stats?.total_orders || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-border/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Revenue</p>
                    <p className="font-heading font-bold text-2xl text-pockett-gray">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-border/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pockett-blue/10 flex items-center justify-center">
                    <Eye className="h-6 w-6 text-pockett-blue" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Pending Orders</p>
                    <p className="font-heading font-bold text-2xl text-pockett-gray">{stats?.pending_orders || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-2xl p-6 border border-border/50">
              <h2 className="font-heading font-semibold text-xl text-pockett-gray mb-4">Recent Orders</h2>
              {orders.length === 0 ? (
                <p className="text-muted-foreground">No orders yet</p>
              ) : (
                <div className="space-y-3">
                  {orders.slice(0, 5).map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                      <div>
                        <p className="font-medium text-pockett-gray">Order #{order.id.slice(0, 8)}</p>
                        <p className="text-sm text-muted-foreground">{order.items.length} items</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-pockett-purple">${order.total.toFixed(2)}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          order.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="font-heading font-bold text-3xl text-pockett-gray">Products</h1>
              <Dialog open={productModal.open} onOpenChange={(open) => setProductModal({ open, product: null })}>
                <DialogTrigger asChild>
                  <Button className="bg-pockett-purple hover:bg-pockett-purple/90 rounded-full" data-testid="add-product-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{productModal.product ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  </DialogHeader>
                  <ProductForm 
                    product={productModal.product}
                    onSuccess={() => {
                      setProductModal({ open: false, product: null });
                      fetchData();
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-2xl border border-border/50 overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium text-pockett-gray">Product</th>
                    <th className="text-left p-4 font-medium text-pockett-gray">Category</th>
                    <th className="text-left p-4 font-medium text-pockett-gray">Price</th>
                    <th className="text-left p-4 font-medium text-pockett-gray">Variants</th>
                    <th className="text-right p-4 font-medium text-pockett-gray">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="border-t border-border/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={product.image_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                          <span className="font-medium text-pockett-gray">{product.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{product.category}</td>
                      <td className="p-4 font-semibold text-pockett-purple">${product.price.toFixed(2)}</td>
                      <td className="p-4 text-muted-foreground">{product.fabric_variants?.length || 0}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setProductModal({ open: true, product })}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteProduct(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-6">
            <h1 className="font-heading font-bold text-3xl text-pockett-gray">Orders</h1>

            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-border/50">
                <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No orders yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white rounded-2xl p-6 border border-border/50">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-pockett-gray">Order #{order.id.slice(0, 8)}</h3>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                        {order.customer_email && (
                          <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Select
                          value={order.status}
                          onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {order.items.map((item, idx) => (
                        <div key={`${order.id}-${item.product_id}-${item.variant_id || idx}`} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                          <img src={item.image_url} alt={item.product_name} className="w-10 h-10 rounded object-cover" />
                          <div className="flex-1">
                            <p className="font-medium text-sm text-pockett-gray">{item.product_name}</p>
                            {item.variant_name && <p className="text-xs text-muted-foreground">{item.variant_name}</p>}
                          </div>
                          <p className="text-sm text-muted-foreground">x{item.quantity}</p>
                          <p className="font-medium text-pockett-purple">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 pt-4 border-t border-border flex justify-end">
                      <p className="font-heading font-bold text-xl text-pockett-purple">
                        Total: ${order.total.toFixed(2)} AUD
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Product Form Component
function ProductForm({ product, onSuccess }) {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    category: product?.category || 'Bags',
    image_url: product?.image_url || '',
    fabric_variants: product?.fabric_variants || []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, data, getAuthHeaders());
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, data, getAuthHeaders());
        toast.success('Product created');
      }
      onSuccess();
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      fabric_variants: [...formData.fabric_variants, { id: Date.now().toString(), name: '', image_url: '', in_stock: true }]
    });
  };

  const updateVariant = (index, field, value) => {
    const variants = [...formData.fabric_variants];
    variants[index][field] = value;
    setFormData({ ...formData, fabric_variants: variants });
  };

  const removeVariant = (index) => {
    setFormData({
      ...formData,
      fabric_variants: formData.fabric_variants.filter((_, i) => i !== index)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Product Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            data-testid="product-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger data-testid="product-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Bags">Bags</SelectItem>
              <SelectItem value="Baby Items">Baby Items</SelectItem>
              <SelectItem value="Accessories">Accessories</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          required
          data-testid="product-description-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="price">Price (AUD)</Label>
          <Input
            id="price"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            required
            data-testid="product-price-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="image_url">Main Image URL</Label>
          <Input
            id="image_url"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            placeholder="https://..."
            required
            data-testid="product-image-input"
          />
        </div>
      </div>

      {/* Fabric Variants */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Fabric Variants</Label>
          <Button type="button" variant="outline" size="sm" onClick={addVariant} data-testid="add-variant-btn">
            <Plus className="h-4 w-4 mr-1" />
            Add Variant
          </Button>
        </div>
        
        {formData.fabric_variants.map((variant, index) => (
          <div key={variant.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Input
              placeholder="Variant name"
              value={variant.name}
              onChange={(e) => updateVariant(index, 'name', e.target.value)}
              className="flex-1"
            />
            <Input
              placeholder="Image URL"
              value={variant.image_url}
              onChange={(e) => updateVariant(index, 'image_url', e.target.value)}
              className="flex-1"
            />
            <Button type="button" variant="ghost" size="icon" onClick={() => removeVariant(index)}>
              <X className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="submit"
        className="w-full bg-pockett-purple hover:bg-pockett-purple/90 rounded-full"
        disabled={loading}
        data-testid="save-product-btn"
      >
        {loading ? 'Saving...' : (product ? 'Update Product' : 'Create Product')}
      </Button>
    </form>
  );
}
